// ============================================
// CARTPAGE.JS - Página completa del carrito
// Ruta: /cart
// ============================================

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useCustomer } from "../context/CustomerContext";
import { useDistributorProgress } from "../hooks/useDistributorProgress";
import { useShippingQuote } from "../hooks/useShippingQuote";
import DistributorProgress from "../components/cart/DistributorProgress";
import ProductBlock from "../components/ProductBlock";
import { useProducts } from "../hooks/useProducts";
import SEO from "../components/SEO";
import PromoCodeModal from "../components/cart/PromoCodeModal";
import "./CartPage.css";
import { formatAmount } from '../utils/money';

// Íconos
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="16" height="16">
    <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="16" height="16">
    <path d="M19 12H5M5 12l7 7M5 12l7-7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function CartPage() {
  const navigate = useNavigate();
  const {
    cart, cartItems, cartCount, subtotal, total, discount, promoCodes, has2x1, ofertasAplicadas,
    removePromoCode, updateQuantity, removeItem,
  } = useCart();
  const { customer } = useCustomer() || {};
  const distProgress = useDistributorProgress(subtotal);
  const isDistributor = distProgress.isDistributor;

  // Distribuidoras: el envío (cajas grandes) se calcula por peso a mano.
  const { quote, request: requestQuote } = useShippingQuote(cart, isDistributor);
  const quoteStatus = quote?.status || "none";
  const quoteReady = quoteStatus === "quoted";
  const [postalCode, setPostalCode] = useState(
    () => customer?.addresses?.[0]?.postal_code || ""
  );
  const [quoteSending, setQuoteSending] = useState(false);
  const [quoteError, setQuoteError] = useState(null);

  const handleRequestQuote = async (e) => {
    e.preventDefault();
    setQuoteSending(true);
    setQuoteError(null);
    try {
      await requestQuote(postalCode.trim());
    } catch (err) {
      setQuoteError(err.message || "No se pudo enviar la solicitud.");
    } finally {
      setQuoteSending(false);
    }
  };

  // Código promocional: el popup central maneja validación, loading y mensajes.
  const [promoOpen, setPromoOpen] = useState(false);

  // Totales: a la distribuidora se le suma el envío cotizado (o nada si aún no).
  // Al público, el envío se elige en el checkout; aquí solo se suma si ya existe.
  const displayTotal = isDistributor
    ? subtotal - discount + (quoteReady ? quote.amount : 0)
    : total;

  const quoteBlocked = isDistributor && !distProgress.checkoutBlocked && !quoteReady;

  // Sugerencias: productos que NO están en el carrito (para hacer la página más viva).
  const { allProducts } = useProducts();
  const cartProductIds = new Set(cartItems.map((it) => it.productId));
  const recommended = (allProducts || []).filter((p) => !cartProductIds.has(p.id)).slice(0, 4);

  return (
    <div className="cartpage-root">
      <SEO title="Carrito" description="Tu carrito de compra Physiology Skincare Co." noindex />
      <div className="cartpage-container">

        {/* Breadcrumb */}
        <nav className="cartpage-breadcrumb">
          <button className="cartpage-back-btn" onClick={() => navigate("/shop")}>
            <ArrowLeftIcon />
            <span>Seguir comprando</span>
          </button>
          <div className="cartpage-steps">
            <span className="step active">Carrito</span>
            <span className="step-divider">›</span>
            <span className="step">Checkout</span>
            <span className="step-divider">›</span>
            <span className="step">Confirmación</span>
          </div>
        </nav>

        <h1 className="cartpage-title">
          Mi carrito <span className="cartpage-count">({cartCount})</span>
        </h1>

        {cartItems.length === 0 ? (
          /* Estado vacío */
          <div className="cartpage-empty">
            <div className="cartpage-empty-icon">∅</div>
            <p className="cartpage-empty-text">Tu carrito está vacío</p>
            <button className="cartpage-empty-btn" onClick={() => navigate("/shop")}>
              Explorar productos
            </button>
          </div>
        ) : (
          <div className="cartpage-layout">

            {/* Columna izquierda: lista de productos */}
            <div className="cartpage-items-col">

              {/* Header columnas */}
              <div className="cartpage-col-header">
                <span className="col-h col-h-product">Producto</span>
                <span className="col-h col-h-price">Precio</span>
                <span className="col-h col-h-qty">Cantidad</span>
                <span className="col-h col-h-total">Total</span>
                <span className="col-h col-h-remove"></span>
              </div>

              {/* Items */}
              {cartItems.map((item) => (
                <div key={item.id} className="cartpage-item">

                  {/* Imagen + Info */}
                  <div className="cartpage-item-product">
                    <div className="cartpage-item-img-wrap">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="cartpage-item-img"
                        />
                      ) : (
                        <div className="cartpage-item-img-placeholder" />
                      )}
                    </div>
                    <div className="cartpage-item-info">
                      <p className="cartpage-item-name">{item.name}</p>
                      {item.size && (
                        <p className="cartpage-item-size">Tamaño: {item.size}</p>
                      )}
                    </div>
                  </div>

                  {/* Precio unitario */}
                  <div className="cartpage-item-price">
                    <span className="cartpage-price-label">Precio</span>
                    <span>${formatAmount(item.price)}</span>
                  </div>

                  {/* Cantidad */}
                  <div className="cartpage-item-qty">
                    <span className="cartpage-price-label">Cantidad</span>
                    <div className="qty-controls">
                      <button
                        className="qty-btn"
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                      >
                        −
                      </button>
                      <span className="qty-value">{item.quantity}</span>
                      <button
                        className="qty-btn"
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Total por item */}
                  <div className="cartpage-item-total">
                    <span className="cartpage-price-label">Total</span>
                    <span className="cartpage-item-total-value">
                      ${formatAmount(item.price * item.quantity)}
                    </span>
                  </div>

                  {/* Eliminar */}
                  <button
                    className="cartpage-remove-btn"
                    onClick={() => removeItem(item.id)}
                    aria-label="Eliminar producto"
                  >
                    <TrashIcon />
                  </button>
                </div>
              ))}
            </div>

            {/* Columna derecha: resumen */}
            <div className="cartpage-summary-col">
              <DistributorProgress {...distProgress} />
              <div className="cartpage-summary-box">
                <h2 className="cartpage-summary-title">Resumen del pedido</h2>

                <div className="cartpage-summary-row">
                  <span>Subtotal</span>
                  <span>${formatAmount(subtotal)}</span>
                </div>

                {discount > 0 && (
                  <div className="cartpage-summary-row cartpage-discount-row">
                    <span>Descuento</span>
                    <span>−${formatAmount(discount)}</span>
                  </div>
                )}

                <div className="cartpage-summary-row">
                  <span>Envío</span>
                  <span className="cartpage-shipping-val">
                    {isDistributor
                      ? quoteReady
                        ? `$${formatAmount(quote.amount)}`
                        : quoteStatus === "requested"
                          ? "En cálculo"
                          : "Por calcular"
                      : cart?.shipping_total
                        ? `$${formatAmount(cart.shipping_total)}`
                        : "Se calcula en el pago"}
                  </span>
                </div>

                {/* Distribuidoras: envío por peso */}
                {isDistributor && (
                  <div className="cartpage-quote">
                    {quoteReady ? (
                      <p className="cartpage-quote-note is-ready">
                        Envío calculado por peso{quote.weight_kg ? ` (${quote.weight_kg} kg)` : ""}.
                        Tu pedido está listo para pago.
                      </p>
                    ) : quoteStatus === "requested" ? (
                      <p className="cartpage-quote-note">
                        Recibimos tu solicitud. Pesamos tu pedido y te avisamos por
                        correo cuando el envío esté calculado.
                      </p>
                    ) : (
                      <>
                        <p className="cartpage-quote-note">
                          {quoteStatus === "stale"
                            ? "Cambiaste tu pedido: solicita de nuevo el cálculo de envío."
                            : "Tus cajas se envían por peso. Solicita el cálculo y te avisamos cuando esté listo."}
                        </p>
                        <form className="cartpage-promo cartpage-quote-form" onSubmit={handleRequestQuote}>
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={5}
                            className="cartpage-promo-input"
                            placeholder="Código postal de entrega"
                            value={postalCode}
                            onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, ""))}
                            aria-label="Código postal de entrega"
                          />
                          <button
                            type="submit"
                            className="cartpage-promo-btn"
                            disabled={quoteSending || postalCode.length !== 5 || distProgress.checkoutBlocked}
                            title={distProgress.checkoutBlocked ? "Primero completa el mínimo de pedido." : undefined}
                          >
                            {quoteSending ? "Enviando…" : "Calcular envío"}
                          </button>
                        </form>
                      </>
                    )}
                    {quoteError && <p className="cartpage-form-error">{quoteError}</p>}
                  </div>
                )}

                <div className="cartpage-summary-divider" />

                <div className="cartpage-summary-row cartpage-summary-total">
                  <span>Total</span>
                  <span>${formatAmount(displayTotal)}</span>
                </div>

                {has2x1 && (
                  <p className="cartpage-promo-2x1">
                    {ofertasAplicadas.map((o) => <span key={o}>{o}</span>)}
                    {ofertasAplicadas.length > 1 ? 'Ofertas aplicadas' : 'Oferta aplicada'} en tu carrito
                  </p>
                )}

                {/* Códigos promocionales aplicados */}
                {promoCodes.length > 0 && (
                  <ul className="cartpage-promo-applied">
                    {promoCodes.map((code) => (
                      <li key={code}>
                        <span>{code}</span>
                        <button type="button" onClick={() => removePromoCode(code)} aria-label={`Quitar ${code}`}>
                          Quitar
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Código promocional → abre popup central (mismo UX que el minicart) */}
                <button
                  type="button"
                  className="cartpage-promo-trigger"
                  onClick={() => setPromoOpen(true)}
                >
                  <span>Código promocional</span>
                  <span className="cartpage-promo-trigger-plus">+</span>
                </button>

                <PromoCodeModal isOpen={promoOpen} onClose={() => setPromoOpen(false)} />

                {/* Botón checkout */}
                <button
                  className="cartpage-checkout-btn"
                  onClick={() => navigate("/checkout")}
                  disabled={cartItems.length === 0 || distProgress.checkoutBlocked || quoteBlocked}
                  title={
                    distProgress.checkoutBlocked
                      ? `Te faltan $${distProgress.missingToMin.toLocaleString('es-MX')} para llegar al mínimo de distribuidora.`
                      : undefined
                  }
                >
                  {distProgress.checkoutBlocked
                    ? `Te faltan $${distProgress.missingToMin.toLocaleString('es-MX')} para finalizar`
                    : quoteBlocked
                      ? 'Esperando cálculo de envío'
                      : 'Proceder al pago'}
                </button>

                {/* Métodos de pago */}
                <div className="cartpage-payment-icons">
                  <span className="pay-icon">VISA</span>
                  <span className="pay-icon">MC</span>
                  <span className="pay-icon">AMEX</span>
                  <span className="pay-icon">PayPal</span>
                </div>
              </div>
            </div>

          </div>
        )}

        {/* Sugerencias: productos que no están en el carrito */}
        {recommended.length > 0 && (
          <div className="cartpage-recommend">
            <ProductBlock
              title="También te puede gustar"
              description="Complementa tu ritual"
              buttonText="Ver todo"
              buttonTo="/shop"
              products={recommended}
            />
          </div>
        )}
      </div>
    </div>
  );
}
